/**
 * StayLineFields — els camps d'una estada (unitat, entrada, sortida, adults, nens, preu) amb el
 * resum «N nits» (runbook 7, F1).
 *
 * Mòdul reutilitzable: rep el `control` d'un formulari de React Hook Form i el `prefix` on viu
 * l'estada (`stays.0`, `stay`, …). No sap si és dins d'un grup: qui l'usa hi posa el títol i el
 * botó de treure (`header`). Els camps han de tenir la forma de `StayLineValues`.
 *
 * El selector d'unitat marca **les ocupades aquelles dates** com a no triables, a partir de les
 * reserves de `['calendar-bookings']` que el calendari ja té carregades (cap crida nova). Fora dels
 * dies carregats no se'n sap res i ho diu: el backend ho torna a comprovar abans de crear.
 *
 * Dins d'un grup, qui l'usa li passa **les altres estades** (`siblings`, opcional): la unitat que ja
 * té una altra línia amb dates solapades surt desactivada («ja és al grup») i, si la triada xoca, l'error
 * es pinta aquí mateix, sense esperar el `superRefine` del formulari (zod no l'executa mentre qualsevol
 * altre camp sigui invàlid). El `superRefine` queda com a xarxa.
 *
 * Sota el preu (que és el **total de l'estada**) surt en viu el desglossament «= 27,50 €/nit × 2 nits».
 *
 * I, si el preu no es pot partir en base i quota al cèntim (estat C de `plans/preus-en-crear-reserves.md`),
 * l'avís amb els dos preus bons més propers (`PriceVatNotice`). Avisa, no bloqueja: es pot desar igualment.
 * El tipus d'IVA ve de `accommodationVatPercent(propertyId)` o de la prop `vatPercent`; mai és a foc aquí.
 */

import { useMemo, type ReactNode } from 'react';
import {
  Controller,
  get,
  useController,
  useFormState,
  useWatch,
  type Control,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { useIsFetching, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { langToLocale } from '@/lib/dates';
import { centsToEuros } from '@guilleumes/types';
import { priceVatNotice } from '@/lib/pricing/price-vat-notice';
import { accommodationVatPercent } from '@/lib/pricing/vat-config';
import { PriceVatNotice } from './PriceVatNotice';
import {
  findStayOverlaps,
  nextDay,
  type StayLineValues,
  type StayRange,
} from '@/lib/schemas/create-reservation';
import type { Booking, Unit } from '@/services/api';

// ============================================
// OCUPACIÓ (pur)
// ============================================

/** El mínim d'una reserva de la graella per saber si ocupa una unitat. */
export type OccupancyBooking = Pick<Booking, 'id' | 'beds24Id' | 'unitId' | 'checkIn' | 'checkOut' | 'status'>;

/**
 * Unitats ocupades entre `checkIn` (inclòs) i `checkOut` (exclòs): `unitId → beds24Id` de la
 * reserva que la trepitja. Les cancel·lades no ocupen; `excludeReservationId` treu la pròpia.
 */
export function occupiedUnitIds(
  bookings: readonly OccupancyBooking[],
  checkIn: string,
  checkOut: string,
  excludeReservationId?: string | null,
): Map<string, string> {
  const occupied = new Map<string, string>();
  if (!checkIn || !checkOut || checkOut <= checkIn) return occupied;
  for (const b of bookings) {
    if (!b.unitId || b.status === 'CANCELLED') continue;
    if (excludeReservationId && b.id === excludeReservationId) continue;
    const bIn = b.checkIn.slice(0, 10);
    const bOut = b.checkOut.slice(0, 10);
    if (bIn < checkOut && checkIn < bOut && !occupied.has(b.unitId)) {
      occupied.set(b.unitId, b.beds24Id);
    }
  }
  return occupied;
}

export interface UnitOption {
  id: string;
  label: string;
  disabled: boolean;
  /** `ocupada`, `ja és al grup` o res. */
  hint?: string;
}

function unitLabel(u: Pick<Unit, 'displayName' | 'name'>): string {
  return u.displayName || u.name;
}

/**
 * Opcions del selector: les actives (més la triada encara que no ho sigui), en ordre físic; les
 * ocupades surten desactivades amb «ocupada» i les que ja té una altra línia del grup aquells dies,
 * amb «ja és al grup» — llevat de la ja triada, perquè es vegi el valor.
 */
export function buildUnitOptions(
  units: readonly Unit[],
  occupied: ReadonlyMap<string, string>,
  selectedUnitId?: string | null,
  inGroup: ReadonlySet<string> = new Set(),
): UnitOption[] {
  return units
    .filter((u) => u.isActive || u.id === selectedUnitId)
    .slice()
    .sort(
      (a, b) =>
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
        unitLabel(a).localeCompare(unitLabel(b), 'ca', { numeric: true }),
    )
    .map((u) => {
      const busy = occupied.has(u.id);
      const taken = inGroup.has(u.id);
      return {
        id: u.id,
        label: unitLabel(u),
        disabled: (busy || taken) && u.id !== selectedUnitId,
        hint: busy ? 'ocupada' : taken ? 'ja és al grup' : undefined,
      };
    });
}

/** Rangs `[from, to]` de les consultes de la graella: `['calendar-bookings', from, to, propertyId]`. */
function readLoadedBookings(
  queryClient: Pick<QueryClient, 'getQueriesData'>,
  propertyId?: number | null,
): { bookings: OccupancyBooking[]; ranges: Array<[string, string]> } {
  const byId = new Map<string, OccupancyBooking>();
  const ranges: Array<[string, string]> = [];
  for (const [key, data] of queryClient.getQueriesData<Booking[]>({ queryKey: ['calendar-bookings'] })) {
    if (!Array.isArray(data)) continue;
    const [, from, to, keyProperty] = key as readonly unknown[];
    if (propertyId && typeof keyProperty === 'number' && keyProperty !== propertyId) continue;
    if (typeof from === 'string' && typeof to === 'string') ranges.push([from.slice(0, 10), to.slice(0, 10)]);
    for (const b of data) byId.set(b.id, b);
  }
  return { bookings: [...byId.values()], ranges };
}

/** Les nits `[checkIn, checkOut)` cauen dins d'algun rang carregat? */
export function isRangeLoaded(ranges: ReadonlyArray<[string, string]>, checkIn: string, checkOut: string): boolean {
  return ranges.some(([from, to]) => from <= checkIn && checkOut <= nextDay(to));
}

/**
 * Ocupació segons el que el calendari ja té carregat (`['calendar-bookings']`). No fa cap crida:
 * `useIsFetching` només serveix perquè es torni a llegir quan la graella acaba de refrescar.
 */
export function useLoadedOccupancy(
  checkIn: string,
  checkOut: string,
  propertyId?: number | null,
  excludeReservationId?: string | null,
): { occupied: Map<string, string>; loaded: boolean } {
  const queryClient = useQueryClient();
  const fetching = useIsFetching({ queryKey: ['calendar-bookings'] });
  return useMemo(() => {
    const { bookings, ranges } = readLoadedBookings(queryClient, propertyId);
    return {
      occupied: occupiedUnitIds(bookings, checkIn, checkOut, excludeReservationId),
      loaded: isRangeLoaded(ranges, checkIn, checkOut),
    };
    // `fetching` és a les dependències a posta: quan canvia, el cache pot haver canviat.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, propertyId, checkIn, checkOut, excludeReservationId, fetching]);
}

/**
 * L'avís de quan les dates surten del que la graella té carregat (`loaded: false`): el selector no
 * marca cap unitat com a ocupada perquè no en sap res, no perquè siguin lliures. `when` = el moment
 * en què es torna a comprovar: en crear la reserva o en desar-ne el canvi.
 */
export function occupancyNotLoadedText(when: 'crear' | 'desar' = 'crear'): string {
  return `Aquests dies no són a la graella carregada: Beds24 comprovarà l'ocupació en ${when}.`;
}

export function OccupancyNotLoadedNotice({
  when = 'crear',
  className,
}: {
  when?: 'crear' | 'desar';
  className?: string;
}) {
  return (
    <p className={cn('text-xs text-gray-500', className)} data-testid="occupancy-not-loaded">
      {occupancyNotLoadedText(when)}
    </p>
  );
}

// ============================================
// XOC DINS DEL GRUP (pur)
// ============================================

/** Una altra estada del mateix grup, amb el nom amb què se l'anomena a l'error («l'habitació 1 del grup»). */
export interface StaySibling extends StayRange {
  label: string;
}

/**
 * Contra les altres estades del grup: les unitats que ja tenen aquelles nits (`inGroup`) i les
 * estades amb què xoca la línia tal com està (`clashes`, via `findStayOverlaps`).
 */
export function groupConflicts(
  current: StayRange,
  siblings: readonly StaySibling[] = [],
): { inGroup: Set<string>; clashes: StaySibling[] } {
  const inGroup = new Set<string>();
  const { data_entrada: checkIn, data_sortida: checkOut } = current;
  if (checkIn && checkOut && checkIn < checkOut) {
    for (const s of siblings) {
      if (s.unit_id && s.data_entrada < checkOut && checkIn < s.data_sortida) inGroup.add(s.unit_id);
    }
  }
  const clashes = findStayOverlaps([current, ...siblings])
    .filter(([i]) => i === 0)
    .map(([, j]) => siblings[j - 1]);
  return { inGroup, clashes };
}

// ============================================
// HELPERS DE PRESENTACIÓ
// ============================================

/** «2 nits · dl. 5 oct. → dc. 7 oct.», o `null` si les dates no fan una estada. */
export function stayLabel(checkIn: string | undefined, checkOut: string | undefined): string | null {
  if (!checkIn || !checkOut) return null;
  const a = parseISO(checkIn);
  const b = parseISO(checkOut);
  if (!isValid(a) || !isValid(b)) return null;
  const nights = differenceInCalendarDays(b, a);
  if (nights < 1) return null;
  const locale = langToLocale('ca');
  const range = `${format(a, 'EEE d MMM', { locale })} → ${format(b, 'EEE d MMM', { locale })}`;
  return `${nights} ${nights === 1 ? 'nit' : 'nits'} · ${range}`;
}

const PER_NIGHT = new Intl.NumberFormat('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * «= 27,50 €/nit × 2 nits»: el preu escrit és el total de l'estada. `null` sense preu > 0 o sense
 * cap nit. Només presentació: no canvia cap càlcul.
 */
export function perNightLabel(price: unknown, checkIn: string | undefined, checkOut: string | undefined): string | null {
  if (typeof price !== 'number' || !Number.isFinite(price) || price <= 0) return null;
  if (!checkIn || !checkOut) return null;
  const a = parseISO(checkIn);
  const b = parseISO(checkOut);
  if (!isValid(a) || !isValid(b)) return null;
  const nights = differenceInCalendarDays(b, a);
  if (nights < 1) return null;
  return `= ${PER_NIGHT.format(price / nights)} €/nit × ${nights} ${nights === 1 ? 'nit' : 'nits'}`;
}

// ============================================
// COMPONENT
// ============================================

export interface StayLineFieldsProps<TForm extends FieldValues> {
  control: Control<TForm>;
  /** On viu l'estada dins del formulari: `stays.0`, `stay`… (els camps, amb la forma de `StayLineValues`). */
  prefix: string;
  /** Prefix dels `id` dels inputs, únic a la pàgina (`cr-0`). */
  idPrefix: string;
  /** Unitats de la propietat (les de `['calendar-units', propertyId]`). */
  units: readonly Unit[];
  propertyId?: number | null;
  /** Reserva que s'està editant, perquè no es marqui ocupada per ella mateixa. */
  excludeReservationId?: string | null;
  /**
   * Les altres estades del grup (opcional): per desactivar les unitats que ja hi són aquells dies i
   * pintar el xoc en viu. Sense `siblings`, la línia no sap res de cap grup.
   */
  siblings?: readonly StaySibling[];
  /**
   * Tipus d'IVA de l'allotjament, en tant per cent, per a l'avís de preu. Per defecte, el de la
   * configuració per a aquesta propietat (`accommodationVatPercent(propertyId)`).
   */
  vatPercent?: number;
  /** Títol i accions de la línia (p. ex. «Habitació 2» i «Treure»). */
  header?: ReactNode;
  disabled?: boolean;
  className?: string;
}

type StayField = keyof StayLineValues;

export function StayLineFields<TForm extends FieldValues>({
  control,
  prefix,
  idPrefix,
  units,
  propertyId,
  excludeReservationId,
  siblings,
  vatPercent,
  header,
  disabled,
  className,
}: StayLineFieldsProps<TForm>) {
  // El component no coneix la forma del formulari sencer: els camins es munten amb el prefix.
  // El cast és el preu de ser genèric; la forma la garanteix qui passa el `prefix`.
  const path = (field: StayField) => `${prefix}.${field}` as Path<TForm>;

  const entry = useController({ control, name: path('data_entrada') });
  const exit = useController({ control, name: path('data_sortida') });
  const watchedUnit: unknown = useWatch({ control, name: path('unit_id') });
  // `useController` i no `useWatch`: a més de llegir el preu, l'avís l'ha de poder escriure.
  const price = useController({ control, name: path('preu_total') });
  const watchedPrice: unknown = price.field.value;
  const checkIn = typeof entry.field.value === 'string' ? entry.field.value : '';
  const checkOut = typeof exit.field.value === 'string' ? exit.field.value : '';
  const unitId = typeof watchedUnit === 'string' ? watchedUnit : '';

  const { errors } = useFormState({ control, name: prefix as Path<TForm> });
  const errorOf = (field: StayField): string | undefined => {
    const message: unknown = get(errors, `${prefix}.${field}.message`);
    return typeof message === 'string' ? message : undefined;
  };

  const { occupied, loaded } = useLoadedOccupancy(checkIn, checkOut, propertyId, excludeReservationId);
  const { inGroup, clashes } = groupConflicts(
    { unit_id: unitId, data_entrada: checkIn, data_sortida: checkOut },
    siblings,
  );
  const options = buildUnitOptions(units, occupied, unitId, inGroup);
  const summary = stayLabel(checkIn, checkOut);
  const perNight = perNightLabel(watchedPrice, checkIn, checkOut);
  const vatNotice = priceVatNotice(watchedPrice, vatPercent ?? accommodationVatPercent(propertyId));
  const selectedBusy = !!unitId && occupied.has(unitId);
  // El xoc en viu mana: si n'hi ha, l'error del `superRefine` (el mateix xoc) no es repeteix.
  const clashError = clashes.length
    ? `Xoca amb ${clashes.map((c) => c.label).join(', ')} (mateixa unitat i dates)`
    : undefined;
  const unitError = clashError ?? errorOf('unit_id');

  const id = (field: string) => `${idPrefix}-${field}`;

  return (
    <fieldset className={cn('space-y-3 rounded-lg border p-3', className)} disabled={disabled}>
      {header}

      <FieldShell id={id('unit')} label="Habitació" error={unitError}>
        <Controller
          control={control}
          name={path('unit_id')}
          render={({ field }) => (
            <Select
              value={typeof field.value === 'string' && field.value !== '' ? field.value : undefined}
              onValueChange={field.onChange}
              disabled={disabled}
            >
              <SelectTrigger
                id={id('unit')}
                aria-labelledby={`${id('unit')}-label`}
                aria-invalid={!!unitError || undefined}
                className={cn('h-9', unitError && 'border-red-500')}
                onBlur={field.onBlur}
              >
                <SelectValue placeholder="Tria una habitació" />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.id} value={o.id} disabled={o.disabled}>
                    <span className={cn(o.disabled && 'text-gray-400')}>{o.label}</span>
                    {o.hint && <span className="ml-2 text-xs text-red-600">{o.hint}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {selectedBusy && (
          <p className="text-xs text-red-600">
            Aquesta habitació està ocupada aquells dies ({occupied.get(unitId)}).
          </p>
        )}
      </FieldShell>

      <div className="grid grid-cols-2 gap-2">
        <FieldShell id={id('in')} label="Entrada" error={errorOf('data_entrada')}>
          <Input
            id={id('in')}
            type="date"
            aria-invalid={!!errorOf('data_entrada') || undefined}
            className={cn('h-9 min-w-0', errorOf('data_entrada') && 'border-red-500')}
            name={entry.field.name}
            ref={entry.field.ref}
            value={checkIn}
            onBlur={entry.field.onBlur}
            onChange={(e) => {
              const value = e.target.value;
              entry.field.onChange(value);
              // Si l'entrada passa la sortida, la sortida es mou a l'endemà (una nit), com en tocar la cel·la.
              if (value && checkOut <= value) exit.field.onChange(nextDay(value));
            }}
          />
        </FieldShell>
        <FieldShell id={id('out')} label="Sortida" error={errorOf('data_sortida')}>
          <Input
            id={id('out')}
            type="date"
            min={checkIn ? nextDay(checkIn) : undefined}
            aria-invalid={!!errorOf('data_sortida') || undefined}
            className={cn('h-9 min-w-0', errorOf('data_sortida') && 'border-red-500')}
            name={exit.field.name}
            ref={exit.field.ref}
            value={checkOut}
            onBlur={exit.field.onBlur}
            onChange={(e) => exit.field.onChange(e.target.value)}
          />
        </FieldShell>
      </div>
      {summary && <p className="text-xs text-gray-500 -mt-1">{summary}</p>}
      {!loaded && checkIn && checkOut && <OccupancyNotLoadedNotice className="-mt-1" />}

      <div className="grid grid-cols-2 gap-2">
        <NumberField control={control} name={path('num_adults')} id={id('adults')} label="Adults" min={1} error={errorOf('num_adults')} />
        <NumberField control={control} name={path('num_nens')} id={id('kids')} label="Nens" min={0} error={errorOf('num_nens')} />
      </div>
      {/* Fila pròpia: l'etiqueta llarga no cap en un terç de 385 px. */}
      <NumberField
        control={control}
        name={path('preu_total')}
        id={id('price')}
        label="Preu total de l'estada (€)"
        min={0}
        step="0.01"
        inputMode="decimal"
        error={errorOf('preu_total')}
      />
      {perNight && (
        <p className="text-xs text-gray-500 -mt-1" aria-live="polite">
          {perNight}
        </p>
      )}
      <PriceVatNotice
        notice={vatNotice}
        disabled={disabled}
        onPick={(cents) => price.field.onChange(centsToEuros(cents))}
      />
    </fieldset>
  );
}

// ============================================
// PECES
// ============================================

function FieldShell({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1 min-w-0">
      <Label id={`${id}-label`} htmlFor={id} className="text-xs text-gray-600 mb-0">
        {label}
      </Label>
      {children}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function NumberField<TForm extends FieldValues>({
  control,
  name,
  id,
  label,
  min,
  step,
  inputMode = 'numeric',
  error,
}: {
  control: Control<TForm>;
  name: Path<TForm>;
  id: string;
  label: string;
  min: number;
  step?: string;
  inputMode?: 'numeric' | 'decimal';
  error?: string;
}) {
  return (
    <FieldShell id={id} label={label} error={error}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Input
            id={id}
            type="number"
            inputMode={inputMode}
            min={min}
            step={step}
            aria-invalid={!!error || undefined}
            className={cn('h-9 min-w-0', error && 'border-red-500')}
            name={field.name}
            ref={field.ref}
            // NaN = buit (el preu s'escriu a mà).
            value={typeof field.value === 'number' && Number.isFinite(field.value) ? field.value : ''}
            onBlur={field.onBlur}
            onChange={(e) => field.onChange(e.target.value === '' ? Number.NaN : e.target.valueAsNumber)}
          />
        )}
      />
    </FieldShell>
  );
}

export default StayLineFields;
